/**
 * ConceptUnlockedToast — carte toast affichée quand l'apprenant découvre
 * un nouveau concept pédagogique (DSO, IS, Amortissement, …).
 *
 * 11/02/2026 (Phase 2 — Pédagogie apprenant).
 *
 * Fonctionnement :
 *   - Lit `pendingConceptToasts` du store.
 *   - Affiche le premier concept en attente pendant ~7 s puis appelle
 *     `markConceptToastShown(id)` pour passer au suivant.
 *   - Un clic sur la carte marque le concept "vu" + ferme le toast.
 *
 * Positionnement : bottom-right (au-dessus de MailShortcutButton).
 */
import React, { useEffect, useState } from 'react';
import { GraduationCap, ChevronRight, X } from 'lucide-react';
import useGameStore from '../../store/gameStore';
import { CONCEPT_BY_ID } from '../../data/pedagogyConcepts';

const CATEGORY_STYLES = {
  Finance: { color: 'text-emerald-300', border: 'border-emerald-500/40', bg: 'bg-emerald-500/10' },
  Commerce: { color: 'text-sky-300', border: 'border-sky-500/40', bg: 'bg-sky-500/10' },
  RH: { color: 'text-amber-300', border: 'border-amber-500/40', bg: 'bg-amber-500/10' },
  Fiscalité: { color: 'text-red-300', border: 'border-red-500/40', bg: 'bg-red-500/10' },
  Stratégie: { color: 'text-purple-300', border: 'border-purple-500/40', bg: 'bg-purple-500/10' },
  Opérationnel: { color: 'text-slate-300', border: 'border-slate-500/40', bg: 'bg-slate-500/10' },
};

const AUTO_DISMISS_MS = 7000;

export default function ConceptUnlockedToast() {
  const pending = useGameStore((s) => s.pendingConceptToasts) || [];
  const markShown = useGameStore((s) => s.markConceptToastShown);
  const markViewed = useGameStore((s) => s.markConceptViewed);
  const [dismissing, setDismissing] = useState(false);

  const currentId = pending[0];
  const concept = currentId ? CONCEPT_BY_ID[currentId] : null;

  useEffect(() => {
    if (!concept) return undefined;
    setDismissing(false);
    const t = setTimeout(() => {
      setDismissing(true);
      // Petit délai pour l'animation de sortie avant de retirer de la file.
      setTimeout(() => markShown(concept.id), 250);
    }, AUTO_DISMISS_MS);
    return () => clearTimeout(t);
  }, [concept, markShown]);

  if (!concept) return null;
  const style = CATEGORY_STYLES[concept.category] || CATEGORY_STYLES.Opérationnel;

  const onClose = () => {
    setDismissing(true);
    setTimeout(() => markShown(concept.id), 200);
  };

  const onViewDetails = () => {
    markViewed(concept.id);
    setDismissing(true);
    setTimeout(() => markShown(concept.id), 200);
  };

  return (
    <div
      className={`fixed bottom-24 right-6 z-[9995] max-w-sm bg-ink-800 border ${style.border} rounded-lg shadow-2xl overflow-hidden transition-all duration-200 ${
        dismissing ? 'opacity-0 translate-x-6' : 'opacity-100 translate-x-0'
      }`}
      data-testid="concept-unlocked-toast"
      data-concept-id={concept.id}
    >
      <div className={`px-3 py-1.5 flex items-center justify-between ${style.bg} border-b ${style.border}`}>
        <div className="flex items-center gap-1.5">
          <GraduationCap className={`w-3.5 h-3.5 ${style.color}`} />
          <span className={`text-[10px] uppercase tracking-widest font-bold ${style.color}`}>
            Concept débloqué · {concept.category}
          </span>
        </div>
        <button
          onClick={onClose}
          className="text-slate-500 hover:text-slate-200"
          aria-label="Fermer"
          data-testid="concept-toast-close"
        >
          <X className="w-3 h-3" />
        </button>
      </div>
      <div className="p-3 space-y-2">
        <h3 className="font-display text-sm font-bold text-foreground tracking-tight">
          {concept.label}
        </h3>
        <p className="text-xs text-slate-400 leading-relaxed">
          {concept.definition}
        </p>
        {concept.trigger_hint && (
          <p className="text-[11px] text-slate-500 italic">
            {concept.trigger_hint}
          </p>
        )}
        <button
          onClick={onViewDetails}
          className={`mt-1 inline-flex items-center gap-1 text-[11px] font-semibold ${style.color} hover:underline`}
          data-testid="concept-toast-view-details"
        >
          Compris <ChevronRight className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
}
